import { useMutation, useQueryClient } from '@tanstack/react-query';

import { notificationService, notificationV2Service } from '@/services/rest';
import { notificationKey } from './queryKeys';

export const useMarkAllNotificationsAsRead = (userId: string) => {
    const qc = useQueryClient();

    return useMutation({
        mutationFn: async () => {
            await notificationV2Service().markAsRead({ userId });
        },

        onSuccess: () => {
            qc.invalidateQueries({
                queryKey: notificationKey.byUser(userId),
            });
        },
    });
};

export const useMarkAsRead = (userId: string) => {
    const qc = useQueryClient();

    return useMutation<void, unknown, string[]>({
        mutationFn: async (notificationIds: string[]) => {
            await notificationService().markAsRead({ notificationIds, userId });
        },

        onMutate: async ids => {
            await qc.cancelQueries({
                queryKey: notificationKey.byUser(userId),
            });

            const prev = qc.getQueriesData<any>({
                queryKey: notificationKey.byUser(userId),
            });

            prev.forEach(([key, data]) => {
                if (!data) return;

                if (data.pages) {
                    const newPages = data.pages.map((p: any) => ({
                        ...p,
                        result: {
                            ...p.result,
                            items: p.result.items.map((it: any) =>
                                ids.includes(it.id)
                                    ? { ...it, isRead: true }
                                    : it
                            ),
                        },
                    }));

                    qc.setQueryData(key, { ...data, pages: newPages });
                }

                if (data.result?.items) {
                    const newItems = data.result.items.map((it: any) =>
                        ids.includes(it.id) ? { ...it, isRead: true } : it
                    );

                    qc.setQueryData(key, {
                        ...data,
                        result: { ...data.result, items: newItems },
                    });
                }
            });

            return { prev };
        },

        onError: (_err, _vars, ctx) => {
            ctx?.prev.forEach(([key, data]) => qc.setQueryData(key, data));
        },

        onSettled: () => {},
    });
};
